import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CloseButton, CrmToaster, IconTrash, Overlay, Pill, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { useCrm } from './useCrm'
import {
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  previewEmailTemplate,
  getDeals,
  type EmailTemplate,
  type EmailBlock,
  type TagGroup,
} from './crmApi'

/**
 * Email Templates — block/section based, ported from the WeQuote portal's
 * notification template editor (settings-wequote-notifications-template-editor):
 *   • body_blocks: an ordered list of typed sections you add/reorder/delete
 *     (Brand header, Eyebrow, Heading, Paragraph, Meta card, CTA button, Image,
 *     Table, Divider, Spacer, Footer)
 *   • {Tag} placeholders picked from grouped tags (Common / Contact / Company /
 *     Lead) — the same convention used when sending to a customer
 *   • live preview rendered server-side with the tags resolved
 */

export const PURPOSES: { id: string; label: string }[] = [
  { id: 'demo_follow_up', label: 'Demo follow-up' },
  { id: 'trial_nurture', label: 'Trial nurture' },
  { id: 'proposal', label: 'Proposal' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'winback', label: 'Win-back' },
  { id: 'general', label: 'General' },
]
export const purposeLabel = (id: string) => PURPOSES.find((p) => p.id === id)?.label || id

const BLOCK_LABEL: Record<string, string> = {
  header: 'Brand header', eyebrow: 'Eyebrow', heading: 'Heading', text: 'Paragraph', meta: 'Meta card',
  button: 'CTA button', image: 'Image', table: 'Table', divider: 'Divider', spacer: 'Spacer', footer: 'Footer',
}
const ADDABLE: EmailBlock['type'][] = ['eyebrow', 'heading', 'text', 'meta', 'button', 'image', 'table', 'divider', 'spacer', 'header', 'footer']

let seq = 0
const nid = () => `b${Date.now().toString(36)}${++seq}`
const newBlock = (type: EmailBlock['type']): EmailBlock => {
  switch (type) {
    case 'header': return { id: nid(), type, brand: 'WeQuote', accent: '#FF4655' }
    case 'eyebrow': return { id: nid(), type, text: 'WEQUOTE' }
    case 'heading': return { id: nid(), type, text: 'Heading' }
    case 'text': return { id: nid(), type, text: 'Hi {First Name},\n\n' }
    case 'meta': return { id: nid(), type, rows: [{ k: 'Company', v: '{Company Name}' }] }
    case 'button': return { id: nid(), type, label: 'Open WeQuote →', url: '{Action URL}', color: '#FF4655' }
    case 'image': return { id: nid(), type, src: '', alt: '' }
    case 'table': return { id: nid(), type, headers: ['Item', 'Value'], cells: [['', '']] }
    case 'spacer': return { id: nid(), type, height: 16 }
    case 'footer': return { id: nid(), type, copyright: '© 2026 Ouitech Ltd.', contactEmail: 'info@wetech.cloud' }
    default: return { id: nid(), type }
  }
}

/* ─── Reusable email preview (iframe-isolated, live, real-lead aware) ─────── */
const EmailPreviewModal: React.FC<{ subject: string; blocks: EmailBlock[]; body?: string; title?: string; onClose: () => void }> = ({ subject, blocks, body, title, onClose }) => {
  const [preview, setPreview] = useState<{ html: string; subject: string; to: string; from: string }>({ html: '', subject: '', to: '', from: '' })
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [dealId, setDealId] = useState('') // '' = sample data
  const [leads, setLeads] = useState<{ id: string; company: string; contact: string }[]>([])

  useEffect(() => {
    getDeals({ limit: 200 })
      .then((r: any) => setLeads((r.deals || []).map((d: any) => ({ id: d.id, company: d.company || d.name, contact: d.contact || '' }))))
      .catch(() => {})
  }, [])

  // Re-renders as the template changes (debounced) — live while editing.
  useEffect(() => {
    const t = setTimeout(() => {
      previewEmailTemplate({ subject, body, bodyBlocks: blocks, dealId: dealId || undefined })
        .then((r) => setPreview({ html: r.html, subject: r.subject, to: r.to, from: r.from }))
        .catch(() => crmToast('Could not render preview', 'error'))
    }, 300)
    return () => clearTimeout(t)
  }, [subject, body, blocks, dealId])

  return (
    <Overlay onClose={onClose} width={860}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-txt-primary truncate">{title || 'Email preview'}</p>
          <p className="text-xs text-txt-tertiary mt-0.5">Exactly as it will render in the inbox.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={dealId}
            onChange={(e) => setDealId(e.target.value)}
            className="rounded-lg border border-border bg-surface px-2 py-1.5 text-xs text-txt-secondary focus:outline-none focus:border-brand-400 max-w-[190px]"
          >
            <option value="">Sample data</option>
            {leads.map((l) => (
              <option key={l.id} value={l.id}>{l.company}{l.contact ? ` · ${l.contact}` : ''}</option>
            ))}
          </select>
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(['desktop', 'mobile'] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDevice(d)}
                className={`px-2.5 py-1.5 text-xs font-medium ${device === d ? 'bg-brand-50 text-brand-700' : 'text-txt-secondary hover:bg-surface-hover'}`}
              >
                {d === 'desktop' ? 'Desktop' : 'Mobile'}
              </button>
            ))}
          </div>
          <CloseButton onClick={onClose} />
        </div>
      </div>

      {/* Inbox chrome — the real From/To/Subject the send will use */}
      <div className="px-5 py-3 border-b border-border bg-surface-secondary space-y-1">
        <div className="flex gap-2 text-xs"><span className="w-14 text-txt-tertiary shrink-0">From</span><span className="text-txt-primary truncate">{preview.from || '—'}</span></div>
        <div className="flex gap-2 text-xs"><span className="w-14 text-txt-tertiary shrink-0">To</span><span className="text-txt-primary truncate">{preview.to || <span className="text-amber-600">no contact email on this lead</span>}</span></div>
        <div className="flex gap-2 text-sm"><span className="w-14 text-txt-tertiary shrink-0 text-xs pt-0.5">Subject</span><span className="font-semibold text-txt-primary truncate">{preview.subject || <span className="text-txt-tertiary font-normal">(no subject)</span>}</span></div>
      </div>

      {/* Isolated iframe so the dashboard's CSS can't leak into the email */}
      <div className="p-4 overflow-auto max-h-[62vh] bg-[#f4f4f4] flex justify-center">
        <iframe
          title="Email preview"
          sandbox=""
          className="bg-[#f4f4f4] border border-border rounded-lg shadow-sm"
          style={{ width: device === 'mobile' ? 375 : 700, height: 560, transition: 'width .15s' }}
          srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;background:#f4f4f4;-webkit-font-smoothing:antialiased}img{max-width:100%}</style></head><body>${preview.html || '<p style="font:14px Arial;color:#9CA3AF;padding:24px">Nothing to preview yet…</p>'}</body></html>`}
        />
      </div>
    </Overlay>
  )
}

export const CrmEmailTemplates: React.FC = () => {
  const { data, reload } = useCrm(getEmailTemplates, { templates: [], purposes: [], tagGroups: [], blockTypes: [] } as any)
  const [editing, setEditing] = useState<EmailTemplate | 'new' | null>(null)
  const [previewing, setPreviewing] = useState<EmailTemplate | null>(null)
  const [filter, setFilter] = useState('all')

  const templates: EmailTemplate[] = data?.templates || []
  const tagGroups: TagGroup[] = data?.tagGroups || []
  const shown = useMemo(() => (filter === 'all' ? templates : templates.filter((t) => t.purpose === filter)), [templates, filter])

  const remove = async (t: EmailTemplate) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return
    try { await deleteEmailTemplate(t.id); crmToast('Template deleted'); reload() } catch { crmToast('Could not delete', 'error') }
  }

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      <header className="px-6 pt-6 pb-4 bg-surface border-b border-border flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-txt-primary">Email Templates</h1>
        </div>
        <Button size="sm" onClick={() => setEditing('new')}>+ New template</Button>
      </header>

      <div className="flex-1 min-h-0 overflow-auto p-6 space-y-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          {[{ id: 'all', label: 'All' }, ...PURPOSES].map((p) => (
            <button
              key={p.id}
              onClick={() => setFilter(p.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${filter === p.id ? 'bg-brand-50 border-brand-300 text-brand-700' : 'border-border text-txt-secondary hover:bg-surface-hover'}`}
            >
              {p.label}
              {p.id !== 'all' && <span className="ml-1.5 text-txt-tertiary">{templates.filter((t) => t.purpose === p.id).length}</span>}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-surface p-12 text-center">
            
            <p className="text-sm text-txt-secondary">No templates yet.</p>
            <Button size="sm" className="mt-3" onClick={() => setEditing('new')}>+ Create your first template</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {shown.map((t) => (
              <div key={t.id} className="rounded-xl bg-surface border border-border p-4 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-txt-primary truncate">{t.name}</p>
                  <Pill tone="blue">{purposeLabel(t.purpose)}</Pill>
                </div>
                <p className="text-xs text-txt-secondary mt-1.5 truncate">{t.subject || <span className="text-txt-tertiary">No subject</span>}</p>
                <p className="text-[11px] text-txt-tertiary mt-2 flex-1">{(t.bodyBlocks?.length || 0)} section{(t.bodyBlocks?.length || 0) === 1 ? '' : 's'}</p>
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" onClick={() => setPreviewing(t)}>Preview</Button>
                  <Button size="sm" variant="secondary" onClick={() => setEditing(t)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t)}><IconTrash className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview straight from the card — no need to open the editor */}
      {previewing && (
        <EmailPreviewModal
          title={previewing.name}
          subject={previewing.subject}
          body={previewing.body}
          blocks={previewing.bodyBlocks || []}
          onClose={() => setPreviewing(null)}
        />
      )}

      {editing && (
        <TemplateEditor
          tpl={editing === 'new' ? null : editing}
          tagGroups={tagGroups}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }}
        />
      )}
    </div>
  )
}

/* ─── Editor: sections + tag picker + live preview ────────────────────────── */
const TemplateEditor: React.FC<{ tpl: EmailTemplate | null; tagGroups: TagGroup[]; onClose: () => void; onSaved: () => void }> = ({ tpl, tagGroups, onClose, onSaved }) => {
  const [name, setName] = useState(tpl?.name || '')
  const [purpose, setPurpose] = useState(tpl?.purpose || 'general')
  const [subject, setSubject] = useState(tpl?.subject || '')
  const [blocks, setBlocks] = useState<EmailBlock[]>(
    tpl?.bodyBlocks?.length ? tpl.bodyBlocks.map((b) => ({ ...b })) : [newBlock('header'), newBlock('heading'), newBlock('text'), newBlock('footer')],
  )
  const [selId, setSelId] = useState<string>(blocks[0]?.id || '')
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  // last focused text input, so a tag inserts where the cursor was
  const lastField = useRef<{ blockId: string; key: string } | null>(null)

  const sel = blocks.find((b) => b.id === selId) || null
  const field = 'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400'

  const setBlock = (id: string, patch: Partial<EmailBlock>) => setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)))
  const addBlock = (type: EmailBlock['type']) => {
    const b = newBlock(type)
    setBlocks((prev) => [...prev, b])
    setSelId(b.id)
  }
  const removeBlock = (id: string) => setBlocks((prev) => prev.filter((b) => b.id !== id))
  const move = (id: string, dir: -1 | 1) =>
    setBlocks((prev) => {
      const i = prev.findIndex((b) => b.id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= prev.length) return prev
      const next = [...prev]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })

  /** Insert a {Tag} into the last-focused field (falls back to the subject). */
  const insertTag = (tag: string) => {
    const target = lastField.current
    if (!target) { setSubject((s) => s + tag); return }
    const b = blocks.find((x) => x.id === target.blockId)
    if (!b) { setSubject((s) => s + tag); return }
    setBlock(b.id, { [target.key]: `${(b as any)[target.key] || ''}${tag}` } as any)
  }

  const doPreview = () => setShowPreview(true)

  const save = async () => {
    if (!name.trim()) return crmToast('Name the template', 'error')
    setSaving(true)
    try {
      const payload = { name: name.trim(), purpose, subject, bodyBlocks: blocks }
      if (tpl) await updateEmailTemplate(tpl.id, payload)
      else await createEmailTemplate(payload)
      crmToast(tpl ? 'Template updated' : 'Template created', 'success')
      onSaved()
    } catch {
      crmToast('Could not save', 'error')
      setSaving(false)
    }
  }

  return (
    <Overlay onClose={onClose} width={1040}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
        <p className="font-semibold text-txt-primary">{tpl ? 'Edit template' : 'New template'}</p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={doPreview}>Preview</Button>
          <CloseButton onClick={onClose} />
        </div>
      </div>

      <div className="px-5 py-4 overflow-auto space-y-4">
        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-txt-secondary">Name</label>
            <input className={field + ' mt-1'} value={name} onChange={(e) => setName(e.target.value)} placeholder="Demo follow-up — day 1" autoFocus />
          </div>
          <div>
            <label className="text-xs font-medium text-txt-secondary">Purpose</label>
            <select className={field + ' mt-1'} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
              {PURPOSES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-txt-secondary">Subject</label>
            <input
              className={field + ' mt-1'}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => (lastField.current = null)}
              placeholder="Thanks for your time, {First Name}"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_220px] gap-4">
          {/* Sections list */}
          <div className="rounded-lg border border-border bg-surface-secondary p-2">
            <p className="text-[11px] uppercase tracking-wide text-txt-tertiary font-semibold px-1 pb-1.5">Sections</p>
            <div className="space-y-1 max-h-72 overflow-auto">
              {blocks.map((b, i) => (
                <div key={b.id} className={`flex items-center gap-1 rounded-md px-2 py-1.5 cursor-pointer ${selId === b.id ? 'bg-brand-50 dark:bg-brand-900/20' : 'hover:bg-surface-hover'}`} onClick={() => setSelId(b.id)}>
                  <span className="text-xs text-txt-primary flex-1 truncate">{BLOCK_LABEL[b.type] || b.type}</span>
                  <button onClick={(e) => { e.stopPropagation(); move(b.id, -1) }} disabled={i === 0} className="text-txt-tertiary hover:text-txt-primary disabled:opacity-30 text-xs">↑</button>
                  <button onClick={(e) => { e.stopPropagation(); move(b.id, 1) }} disabled={i === blocks.length - 1} className="text-txt-tertiary hover:text-txt-primary disabled:opacity-30 text-xs">↓</button>
                  <button onClick={(e) => { e.stopPropagation(); removeBlock(b.id) }} className="text-txt-tertiary hover:text-red-600 text-xs">✕</button>
                </div>
              ))}
            </div>
            <select className="w-full mt-2 rounded-md border border-dashed border-border bg-surface px-2 py-1.5 text-xs text-txt-secondary" value="" onChange={(e) => e.target.value && addBlock(e.target.value as EmailBlock['type'])}>
              <option value="">+ Add section…</option>
              {ADDABLE.map((t) => <option key={t} value={t}>{BLOCK_LABEL[t]}</option>)}
            </select>
          </div>

          {/* Selected block fields */}
          <div className="rounded-lg border border-border p-3 min-h-[18rem]">
            {!sel ? (
              <p className="text-sm text-txt-tertiary">Select a section on the left.</p>
            ) : (
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-wide text-txt-tertiary font-semibold">{BLOCK_LABEL[sel.type]}</p>
                {(sel.type === 'eyebrow' || sel.type === 'heading' || sel.type === 'text') && (
                  <textarea
                    className={field + ' resize-y'}
                    rows={sel.type === 'text' ? 8 : 2}
                    value={sel.text || ''}
                    onFocus={() => (lastField.current = { blockId: sel.id, key: 'text' })}
                    onChange={(e) => setBlock(sel.id, { text: e.target.value })}
                  />
                )}
                {sel.type === 'header' && (
                  <div className="grid grid-cols-2 gap-2">
                    <input className={field} value={sel.brand || ''} placeholder="Brand" onFocus={() => (lastField.current = { blockId: sel.id, key: 'brand' })} onChange={(e) => setBlock(sel.id, { brand: e.target.value })} />
                    <input className={field} type="color" value={sel.accent || '#FF4655'} onChange={(e) => setBlock(sel.id, { accent: e.target.value })} />
                  </div>
                )}
                {sel.type === 'button' && (
                  <div className="grid grid-cols-2 gap-2">
                    <input className={field} value={sel.label || ''} placeholder="Label" onFocus={() => (lastField.current = { blockId: sel.id, key: 'label' })} onChange={(e) => setBlock(sel.id, { label: e.target.value })} />
                    <input className={field} value={sel.url || ''} placeholder="{Action URL}" onFocus={() => (lastField.current = { blockId: sel.id, key: 'url' })} onChange={(e) => setBlock(sel.id, { url: e.target.value })} />
                  </div>
                )}
                {sel.type === 'image' && (
                  <div className="grid grid-cols-2 gap-2">
                    <input className={field} value={sel.src || ''} placeholder="Image URL" onChange={(e) => setBlock(sel.id, { src: e.target.value })} />
                    <input className={field} value={sel.alt || ''} placeholder="Alt text" onChange={(e) => setBlock(sel.id, { alt: e.target.value })} />
                  </div>
                )}
                {sel.type === 'spacer' && (
                  <input className={field} type="number" min={4} max={80} value={sel.height ?? 16} onChange={(e) => setBlock(sel.id, { height: Number(e.target.value) })} />
                )}
                {sel.type === 'meta' && (
                  <div className="space-y-2">
                    {(sel.rows || []).map((r, i) => (
                      <div key={i} className="flex gap-2">
                        <input className={field} value={r.k} placeholder="Label" onChange={(e) => { const rows = [...(sel.rows || [])]; rows[i] = { ...rows[i], k: e.target.value }; setBlock(sel.id, { rows }) }} />
                        <input className={field} value={r.v} placeholder="{Company Name}" onChange={(e) => { const rows = [...(sel.rows || [])]; rows[i] = { ...rows[i], v: e.target.value }; setBlock(sel.id, { rows }) }} />
                        <button className="text-txt-tertiary hover:text-red-600 px-1" onClick={() => setBlock(sel.id, { rows: (sel.rows || []).filter((_, j) => j !== i) })}>✕</button>
                      </div>
                    ))}
                    <Button size="sm" variant="secondary" onClick={() => setBlock(sel.id, { rows: [...(sel.rows || []), { k: '', v: '' }] })}>+ Row</Button>
                  </div>
                )}
                {sel.type === 'table' && (
                  <div className="space-y-2">
                    <input className={field} value={(sel.headers || []).join(', ')} placeholder="Headers (comma separated)" onChange={(e) => setBlock(sel.id, { headers: e.target.value.split(',').map((s) => s.trim()) })} />
                    {(sel.cells || []).map((row, i) => (
                      <div key={i} className="flex gap-2">
                        <input className={field} value={row.join(', ')} placeholder="Cells (comma separated)" onChange={(e) => { const cells = [...(sel.cells || [])]; cells[i] = e.target.value.split(',').map((s) => s.trim()); setBlock(sel.id, { cells }) }} />
                        <button className="text-txt-tertiary hover:text-red-600 px-1" onClick={() => setBlock(sel.id, { cells: (sel.cells || []).filter((_, j) => j !== i) })}>✕</button>
                      </div>
                    ))}
                    <Button size="sm" variant="secondary" onClick={() => setBlock(sel.id, { cells: [...(sel.cells || []), ['', '']] })}>+ Row</Button>
                  </div>
                )}
                {sel.type === 'footer' && (
                  <div className="space-y-2">
                    <input className={field} value={sel.copyright || ''} placeholder="Copyright" onChange={(e) => setBlock(sel.id, { copyright: e.target.value })} />
                    <input className={field} value={sel.companyInfo || ''} placeholder="Company info" onChange={(e) => setBlock(sel.id, { companyInfo: e.target.value })} />
                    <input className={field} value={sel.address || ''} placeholder="Address" onChange={(e) => setBlock(sel.id, { address: e.target.value })} />
                    <input className={field} value={sel.contactEmail || ''} placeholder="Contact email" onChange={(e) => setBlock(sel.id, { contactEmail: e.target.value })} />
                  </div>
                )}
                {sel.type === 'divider' && <p className="text-xs text-txt-tertiary">A horizontal rule. Nothing to configure.</p>}
              </div>
            )}
          </div>

          {/* Tag picker */}
          <div className="rounded-lg border border-border bg-surface-secondary p-2">
            <p className="text-[11px] uppercase tracking-wide text-txt-tertiary font-semibold px-1 pb-1">Tags</p>
            <p className="text-[10px] text-txt-tertiary px-1 pb-2">Click to insert into the focused field. Unknown tags render empty.</p>
            <div className="space-y-2 max-h-72 overflow-auto">
              {tagGroups.map((g) => (
                <div key={g.key}>
                  <p className="text-[10px] font-semibold text-txt-secondary px-1">{g.label}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {g.tags.map((t) => (
                      <button key={t} onClick={() => insertTag(t)} className="px-1.5 py-0.5 rounded border border-border bg-surface text-[10px] font-mono text-txt-secondary hover:bg-surface-hover">
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" loading={saving} onClick={save}>{tpl ? 'Save changes' : 'Create template'}</Button>
      </div>

      {showPreview && (
        <EmailPreviewModal
          title={name || 'Email preview'}
          subject={subject}
          blocks={blocks}
          onClose={() => setShowPreview(false)}
        />
      )}
    </Overlay>
  )
}
